<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="data.results"
      item-key="name"
      class="datagrid"
      hide-default-footer
      hide-default-header
      :options="{ itemsPerPage: paging.page_size + 2 }"
    >
      <template v-slot:header="{ props: { headers } }">
        <ReviewsCategoriesTableHeader
          v-for="header in headers"
          :key="header.id"
          :header="header"
          @sort="sort"
          @reset-sort-desc="resetSortDesc"
        />
      </template>
      <template v-slot:item="{ item, headers }">
        <ReviewsCategoriesTableRow
          :headers="headers"
          :item="item"
          :is-row-action-allowed="isRowActionAllowed(item)"
          @go-to-details="$emit('go-to-details', $event)"
          @go-to-list="$emit('go-to-list', $event)"
        >
          <template v-slot="{ columnName, item }">
            <slot
              name="customColumn"
              :item="item"
              :headers="headers"
              :column-name="columnName"
            />
          </template>
        </ReviewsCategoriesTableRow>
      </template>
    </v-data-table>
    <Pagination
      class="datagrid__pagination"
      :page="paging.page"
      :per-page="paging.page_size"
      :options="pageOptions"
      :total-count="data.count"
      show-options
      centered
      @change="onPageChangeHandler"
      @onPerPageChange="onPageChangeHandler"
    />
  </div>
</template>

<script>
import Pagination from "@/components/List/Pagination.vue";
import ReviewsCategoriesTableHeader from "./ReviewsCategoriesTableHeader.vue";
import ReviewsCategoriesTableRow from "./ReviewsCategoriesTableRow.vue";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import Vue from "vue";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";

export default {
  name: "ReviewsCategoriesTable",
  components: {
    Pagination,
    ReviewsCategoriesTableHeader,
    ReviewsCategoriesTableRow
  },
  props: {
    headers: {
      type: Array,
      required: true
    },
    data: {
      type: [Object, null],
      required: true
    },
    name: {
      type: String
    },
    paging: {
      type: Object,
      required: true
    },
    disabledListActionRowIndex: {
      type: Number
    }
  },
  data() {
    return {
      sortBy: null,
      sortDesc: null,
      pageOptions: [10, 25, 50, 100, 200],
      sortOrder: null,
      orderDirection: null
    };
  },
  methods: {
    isRowActionAllowed(item) {
      if (!this.disabledListActionRowIndex) return true;
      return (
        this.data.results.indexOf(item) !== this.disabledListActionRowIndex
      );
    },

    sort(headerColumn) {
      this.sortBy = headerColumn.sortBy;
      this.sortDesc = headerColumn.sortDesc;
      this.orderDirection = headerColumn.orderDirection;
      this.sortOrder = headerColumn.sortOrder;
      this.$emit("sort", {
        order_by: this.sortBy,
        ...this.orderDirection
      });
      this.trackSortEvent(headerColumn);
    },
    resetSortDesc() {
      this.headers.forEach(item => {
        Vue.set(item, "sortDesc", null);
      });
    },

    trackSortEvent(headerColumn) {
      const isDetailsPage = this.$route.name === "reviews-category-details";
      this.$gtm.trackCustomEvent({
        event: isDetailsPage
          ? GTM_EVENT_NAME.REVIEW_CATEGORY_ANALYTICS_DETAILS_TABLE_SORT
          : GTM_EVENT_NAME.REVIEW_CATEGORY_ANALYTICS_OVERVIEW_TABLE_SORT,
        table: this.name,
        sort_order: headerColumn.sortBy ? headerColumn.sortOrder : "default",
        column: headerColumn.eventActionName
      });
    },
    onPageChangeHandler({ page, perPage }) {
      this.changePaging({
        page,
        page_size: perPage
      });
    },
    changePaging(paging) {
      this.$emit("page-change", paging);
    },

    resetPaging() {
      this.changePaging({
        page: 1,
        perPage: this.pageOptions[0]
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.table-wrapper {
  position: relative;
  z-index: 0;
}
::v-deep .text-primary {
  color: $primary;
}
.search-field {
  ::v-deep .v-label {
    color: $primary;
  }
}
::v-deep .datagrid {
  min-height: 570px;
  &__sort-btn {
    display: flex;
    align-items: center;
    margin-left: $spacing * 2;
  }

  ::v-deep &__sort-icon {
    color: $grey-darker;

    &--active {
      color: $primary;
    }

    &--in-active {
      color: $gray-2;
    }

    &--down {
      transform: rotate(180deg);
    }
  }

  &__star-icon {
    color: $lime;
    width: 14px;
  }
  &__pagination {
    margin-top: $spacing * 2;
  }

  &__rating {
    display: flex;
    align-items: center;
    justify-content: center;
    span {
      margin-right: $spacing;
    }
  }

  &__arrow-down {
    transform: rotate(180deg);
  }

  &__row:nth-child(odd) {
    background: #fafafc;

    &:hover {
      background: #eeeeee;
    }
  }

  .datagrid__cell {
    border: none !important;
    padding: $spacing * 2.75 $spacing !important;
    @include font-size(16px);
    text-align: center;
    position: relative;
    z-index: 2;
    vertical-align: middle;

    * {
      vertical-align: middle;
    }

    &:first-child {
      text-align: left;
    }

    &--primary {
      color: $primary;
    }

    &--value {
      margin-right: $spacing * 1.5;
      @include font-size(18px);
      line-height: 18px;
    }
  }

  &__dropdown {
    width: 400px;
    height: 200px;
    background: red;
    position: absolute;
    right: 0;
  }

  ::v-deep .v-data-table-header {
    th {
      @include font-size(16px);
      font-weight: 500;
      color: #85868b !important;
      border: none !important;
      text-align: center;

      &:first-child {
        padding-left: $spacing;
      }
    }
  }
}
</style>
